import { CSSProperties } from "react";
import { Toaster, toast } from "sonner";
import {
  IconAlertTriangle,
  IconCheck,
  IconInfoCircle,
  IconLoader2,
  IconPhoto,
  IconX,
  type Icon,
} from "@tabler/icons-react";

/**
 * The mark a toast wears.
 *
 * Four of them are verdicts on what just happened. `visual_abstract` is not: it
 * names the THING the toast is about, because "your poster is ready" is not a
 * success report, it is a delivery — and a green tick beside it reads as
 * "operation completed" rather than "there is a poster to look at".
 * `generating` is not a verdict either: it says work is under way.
 */
const TOAST_MARKS = {
  success: {
    Glyph: IconCheck,
    badge: "bg-ds-mint-100",
    glyph: "text-ds-mint-800",
  },
  error: {
    Glyph: IconX,
    badge: "bg-ds-clementine-100",
    glyph: "text-ds-clementine-600",
  },
  warning: {
    Glyph: IconAlertTriangle,
    badge: "bg-ds-clementine-100",
    glyph: "text-ds-clementine-600",
  },
  info: {
    Glyph: IconInfoCircle,
    badge: "bg-ds-slate-100",
    glyph: "text-ds-slate-500",
  },
  visual_abstract: {
    Glyph: IconPhoto,
    badge: "bg-ds-mint-100",
    glyph: "text-ds-mint-800",
  },
  // Work the reader started and can walk away from: a poster being generated.
  // The spinner says "under way", where a tick would say "done".
  generating: {
    Glyph: IconLoader2,
    badge: "bg-ds-slate-100",
    glyph: "animate-spin text-ds-slate-500",
  },
} as const satisfies Record<
  string,
  { Glyph: Icon; badge: string; glyph: string }
>;

export type ToastStatus = keyof typeof TOAST_MARKS;

// Sonner reads the stack width from a `--width` custom property; keep toasts
// near full-width on phones while capping at the 356px design size.
const toasterStyle: CSSProperties & Record<`--${string}`, string> = {
  "--width": "min(22.25rem, calc(100vw - 2rem))",
};

// The desktop stack is wider (Figma "UX/UI Desktop Version": 404 to 478px):
// its toasts carry actions ("View", "Undo", "Try again"), and at the phone
// width the sentence beside them was cut after two lines.
const desktopToasterStyle: CSSProperties & Record<`--${string}`, string> = {
  "--width": "min(30rem, calc(100vw - 2rem))",
};

// An unstyled toast is as wide as its content, and the one-line subtitle's
// content is the whole paper title: without this, a long title stretched the
// card far past `--width`, off the stack's centre line.
const toastOptions = {
  unstyled: true,
  style: { width: "var(--width)" },
} as const;

/**
 * The single toast surface, mounted once at the app root. Sonner owns the
 * enter/exit animation, stacking and swipe-to-dismiss; a top-anchored stack
 * dismisses by swiping *up*, matching the Design System 5.0 toast spec.
 *
 * The desktop shell anchors the stack at the top right instead: the corner
 * the eye goes to for a notice on a wide screen, clear of the page's
 * breadcrumb, and of the Quick task composer at the bottom right. It sits
 * under the page's top bar, beside its search trigger. The app root picks the
 * placement, which keeps this file free of the platform detection.
 */
export function AppToaster({
  placement,
}: {
  placement: "top-center" | "top-right";
}): React.ReactElement {
  if (placement === "top-right")
    return (
      <Toaster
        position="top-right"
        swipeDirections={["top", "right"]}
        offset={{ top: "4.5rem", right: "1.5rem" }}
        style={desktopToasterStyle}
        toastOptions={toastOptions}
      />
    );
  // Sonner applies `mobileOffset` (not `offset`) below a 600px viewport, so the
  // in-app webview needs both set to clear the status bar / notch.
  const topOffset = { top: "calc(var(--safe-area-top) + 0.5rem)" };
  return (
    <Toaster
      position="top-center"
      swipeDirections={["top"]}
      offset={topOffset}
      mobileOffset={topOffset}
      style={toasterStyle}
      toastOptions={toastOptions}
    />
  );
}

/**
 * What a toast says: one line that names the event, and an optional second that
 * names the OBJECT it happened to — a paper's title, a playlist's name.
 *
 * They are two fields rather than one sentence because the object is not ours
 * to keep short. A push announcing a poster carries the paper's title, and a
 * journal title runs to 150 characters: joined into the message, that produced
 * a six-line slab over the whole screen. Split, the subtitle is truncated to
 * one line and the card cannot grow past three lines whatever arrives in it.
 */
type ToastContent = { title: string; description: string | null };

/**
 * A text button at the end of the toast, for what the reader may still do about
 * the event: undo it, open what it produced, try it again. The first action is
 * the primary one and wears the brand colour.
 */
type ToastAction = { label: string; onClick: () => void };

function ToastCard({
  status,
  content,
  actions,
  onRowClick,
  onClose,
}: {
  status: ToastStatus;
  content: ToastContent;
  actions: readonly ToastAction[];
  onRowClick: (() => void) | null;
  onClose: () => void;
}): React.ReactElement {
  const { Glyph, badge, glyph } = TOAST_MARKS[status];
  return (
    <div
      className="flex w-full items-center gap-3 rounded-xl border border-black/5 bg-white/90 p-3 shadow-toast backdrop-blur-md"
      onClick={() => {
        if (onRowClick !== null) onRowClick();
        onClose();
      }}
    >
      {/* A rounded SQUARE, not a disc: the mark names the object the toast is
          about (a poster, a paper), and a disc reads as a status pip. */}
      <div
        className={`flex size-11 shrink-0 items-center justify-center rounded-2xl ${badge}`}
      >
        <Glyph className={`size-5 ${glyph}`} stroke={2} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* The title may wrap to a second line rather than lose a word: a push
            writes it and some are a full sentence. The subtitle never does. It
            carries a journal title of arbitrary length and is there to say
            WHICH object, not to be read whole. */}
        <p className="m-0 line-clamp-2 font-semibold text-ui-body wrap-break-word text-primary-black">
          {content.title}
        </p>
        {content.description !== null && (
          <p className="m-0 truncate text-ui-note text-ds-slate-600">
            {content.description}
          </p>
        )}
      </div>
      {actions.map((action, index) => (
        <button
          key={action.label}
          type="button"
          className={`shrink-0 cursor-pointer border-none bg-transparent p-0 text-ui-body font-semibold ${index === 0 ? "text-ds-primary" : "text-ds-slate-600"} hover:underline`}
          onClick={(event) => {
            event.stopPropagation();
            action.onClick();
            onClose();
          }}
        >
          {action.label}
        </button>
      ))}
      <button
        type="button"
        aria-label="Dismiss"
        // Top-aligned beside a two-line block, centred beside a one-line one:
        // a cross floating mid-height next to a single sentence reads as
        // misplaced.
        className={`shrink-0 text-ds-slate-400 ${content.description === null ? "" : "self-start"}`}
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        <IconX className="size-4.5" stroke={2} />
      </button>
    </div>
  );
}

// Message length drives dwell time so short confirmations don't linger and long
// notices stay readable — clamped so nothing sits forever.
function toastDuration(content: ToastContent): number {
  const length =
    content.title.length +
    (content.description === null ? 0 : content.description.length);
  return Math.min(3000 + length * 30, 5000);
}

function renderToast(
  content: ToastContent,
  status: ToastStatus,
  actions: readonly ToastAction[],
  onRowClick: (() => void) | null
): void {
  toast.custom(
    (id) => (
      <ToastCard
        status={status}
        content={content}
        actions={actions}
        onRowClick={onRowClick}
        onClose={() => toast.dismiss(id)}
      />
    ),
    // Keying on the text dedupes identical toasts fired in quick succession.
    {
      // An action belongs to one event: two "Removed…" toasts in a row each
      // keep their own Undo rather than the second replacing the first.
      id:
        actions.length === 0
          ? `${content.title}\n${content.description === null ? "" : content.description}`
          : crypto.randomUUID(),
      // An action needs time to be read and reached for.
      duration: actions.length === 0 ? toastDuration(content) : 6000,
    }
  );
}

export function displayToast(message: string, status: ToastStatus): void {
  renderToast({ title: message, description: null }, status, [], null);
}

/**
 * A toast that offers the reader one or two follow-ups on what just happened
 * ("Undo", "View", "Try again"). Clicking an action closes the toast.
 */
export function displayToastWithActions(
  message: string,
  status: ToastStatus,
  actions: readonly ToastAction[]
): void {
  renderToast({ title: message, description: null }, status, actions, null);
}

/**
 * A toast whose whole row is actionable — used for in-app notifications that
 * navigate on tap. Kept separate from `displayToast` so passive toasts never
 * carry a dangling optional callback. No button: the card IS the affordance,
 * and a CTA beside a row that already navigates offers one thing twice.
 */
export function displayActionableToast(
  content: ToastContent,
  status: ToastStatus,
  onRowClick: () => void
): void {
  renderToast(content, status, [], onRowClick);
}
